import PropTypes from "prop-types";
import React from "react";
import Info from "./Info";
import Notification from "./Notification";
import QuotaLimit from "./quota/QuotaLimit";
import CollectionSelect from "./CollectionSelect";
import {
  Box,
  Flex,
  Image,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";

import { useStylesGeneral } from "../utils/mantine/stampTabContent/useStylesGeneral";
import CustomButton from "./mantineCustomComponents/CustomButton";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";

const StampContentId = ({
  currentScreen,
  staticBasePath,
  handleRemoveHash,
  isSmallScreen,
  isAuthenticated,
  isValid,
  objectHash,
  handleHashChange,
  handleCreateSubmit,
  buttonStampClassName,
  noQuotaAvailable,
  onSelectedOptionCollection,
  selectedOptionCollection,
  activateSelectCollection,
  onActivateSelectCollection,
  helpIsActive,
  selectCollections,
}) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // css classes
  const { classes: GeneralClasses } = useStylesGeneral();

  return (
    <>
      <Box id="verify-method-hash__screen" className={GeneralClasses.wrap}>
        <Box className={GeneralClasses.wrapContent}>
          {currentScreen === "found" && (
            <Info
              textInfo="You have already stamped this file previously. You can choose another file or stamp this content ID again."
              staticBasePath={staticBasePath}
            />
          )}
          <Box className={GeneralClasses.stampFromHashContainer}>
            {!isAuthenticated && !isSmallScreen && <Notification />}
            {noQuotaAvailable && <QuotaLimit />}
            <Box className={GeneralClasses.stampFromHashInputWrap}>
              <TextInput
                id="object-hash"
                className={`${GeneralClasses.stampFromHashInput} ${
                  isValid ? "" : GeneralClasses.stampFromHashInputInvalid
                } ${
                  !isAuthenticated || noQuotaAvailable
                    ? GeneralClasses.stampFromHashInputDisabled
                    : ""
                }`}
                placeholder="Enter content ID"
                disabled={!isAuthenticated || noQuotaAvailable}
                value={objectHash || ""}
                onChange={handleHashChange}
                pattern="^(0x)?[0-9a-fA-F]{64}$"
              />
              {objectHash && (
                <Image
                  onClick={handleRemoveHash}
                  src={`${staticBasePath}public/images/svg/rb-remove-hash.svg`}
                  alt="Remove Hash"
                  className={GeneralClasses.stampFromHashInputImg}
                />
              )}

              {!isValid && (
                <Box className={GeneralClasses.stampFromHashInputInvalidText}>
                  Invalid content ID format.
                </Box>
              )}

              {isSmallScreen && (
                <Box className={GeneralClasses.stampInfo}>
                  <CustomButton
                    text="Make a vBase Stamp!"
                    onClick={handleCreateSubmit}
                    disabled={
                      !(
                        (activateSelectCollection === false ||
                          selectedOptionCollection) &&
                        objectHash
                      )
                    }
                    componentClasses={buttonStampClassName}
                  />

                  {!isAuthenticated && (
                    <Box className={GeneralClasses.stampHashMobileLogin}>
                      <CustomAnchor
                        text="Sign In"
                        href="/accounts/login/"
                        componentClasses="link"
                      />
                      <Text component="span"> to Start Stamping!</Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className={GeneralClasses.stampFromHashContainer}>
        <Flex
          justify={theme.flex.justify.center}
          align={theme.flex.align.center}
          className={`${GeneralClasses.stampHashText} ${GeneralClasses.stampHashTextFirst} `}
        >
          <Image
            src={`${staticBasePath}public/images/svg/rb-info-grey.svg`}
            alt="Info"
          />
          <Text>
            <Text component="span">This is an advanced feature and</Text> not
            recommended for typical users.
          </Text>
        </Flex>
        <Flex
          justify={theme.flex.justify.center}
          align={theme.flex.align.center}
          className={GeneralClasses.stampHashText}
        >
          <Image
            src={`${staticBasePath}public/images/svg/rb-info-grey.svg`}
            alt="Info"
          />
          <Text>
            <Text component="span">
              {" "}
              Please be sure that your content ID matches a file you saved
              locally.
            </Text>
          </Text>
        </Flex>

        <Flex
          justify={theme.flex.justify.center}
          align={theme.flex.align.center}
          className={GeneralClasses.stampHashText}
        >
          <Image
            src={`${staticBasePath}public/images/svg/rb-info-grey.svg`}
            alt="Info"
          />
          <Text>
            If this process fails, your content ID will not be validated.
          </Text>
        </Flex>

        <Flex
          justify={theme.flex.justify.center}
          align={theme.flex.align.center}
          className={GeneralClasses.stampHashText}
        >
          <Image
            src={`${staticBasePath}public/images/svg/rb-info-grey.svg`}
            alt="Info"
          />
          <Text>
            If you need help creating a robust workflow to stamp content IDs,{" "}
            <CustomAnchor
              text="please contact us"
              href="https://www.vbase.com/contact/"
              target="_blank"
              componentClasses="link"
            />
            .
          </Text>
        </Flex>
        {selectCollections && isAuthenticated && (
          <CollectionSelect
            activateSelectCollection={activateSelectCollection}
            onActivateSelectCollection={onActivateSelectCollection}
            onActivateSelectBCollection={onActivateSelectCollection}
            selectCollections={selectCollections}
            onSelectedOptionCollection={onSelectedOptionCollection}
            selectedOptionCollection={selectedOptionCollection}
            helpIsActive={helpIsActive}
          />
        )}
      </Box>
    </>
  );
};
StampContentId.propTypes = {
  currentScreen: PropTypes.string.isRequired,
  staticBasePath: PropTypes.string.isRequired,
  handleRemoveHash: PropTypes.func.isRequired,
  isSmallScreen: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  objectHash: PropTypes.string,
  handleHashChange: PropTypes.func.isRequired,
  handleCreateSubmit: PropTypes.func.isRequired,
  buttonStampClassName: PropTypes.string.isRequired,
  noQuotaAvailable: PropTypes.bool.isRequired,
  onSelectedOptionCollection: PropTypes.func.isRequired,
  selectedOptionCollection: PropTypes.object,
  activateSelectCollection: PropTypes.bool.isRequired,
  onActivateSelectCollection: PropTypes.func.isRequired,
  helpIsActive: PropTypes.bool.isRequired,
  selectCollections: PropTypes.string,
};
export default StampContentId;
